import api, { handleApiResponse, handleApiError } from './api';

class EscalationService {
  // Get comprehensive escalation config (matches controller's getConfig)
  async getConfig() {
    try {
      const response = await api.get('/escalation/config');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update comprehensive config (matches controller's updateConfig)
  async updateConfig(data) {
    try {
      const response = await api.post('/escalation/config', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get priority-specific configuration
  async getPriorityConfig() {
    try {
      const response = await api.get('/escalation/priority-config');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update priority configuration
  async updatePriorityConfig(data) {
    try {
      const response = await api.post('/escalation/priority-config', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get business hours configuration
  async getBusinessHoursConfig() {
    try {
      const response = await api.get('/escalation/business-hours-config');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update business hours configuration
  async updateBusinessHoursConfig(data) {
    try {
      const response = await api.post('/escalation/business-hours-config', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get auto-forward configuration
  async getAutoForwardConfig() {
    try {
      const response = await api.get('/escalation/auto-forward-config');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update auto-forward configuration
  async updateAutoForwardConfig(data) {
    try {
      const response = await api.post('/escalation/auto-forward-config', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Calculate forward time for specific scenario
  async calculateForwardTime(data) {
    try {
      const response = await api.post('/escalation/calculate-forward-time', data);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get escalation analytics
  async getEscalationAnalytics(params = {}) {
    try {
      const response = await api.get('/escalation/analytics', { params });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Utility methods
  getPriorityOptions() {
    return [
      { value: 'low', label: 'Low', color: 'gray' },
      { value: 'medium', label: 'Medium', color: 'blue' },
      { value: 'high', label: 'High', color: 'orange' },
      { value: 'critical', label: 'Critical', color: 'red' }
    ];
  }

  getTimeSuggestions() {
    return [
      { value: 15, label: '15 minutes (Very Fast)' },
      { value: 30, label: '30 minutes (Default)' },
      { value: 60, label: '1 hour (Standard)' },
      { value: 120, label: '2 hours (Relaxed)' },
      { value: 240, label: '4 hours (Extended)' }
    ];
  }

  getBusinessDayOptions() {
    return [
      { value: 'monday', label: 'Monday' },
      { value: 'tuesday', label: 'Tuesday' },
      { value: 'wednesday', label: 'Wednesday' },
      { value: 'thursday', label: 'Thursday' },
      { value: 'friday', label: 'Friday' },
      { value: 'saturday', label: 'Saturday' },
      { value: 'sunday', label: 'Sunday' }
    ];
  }

  formatTime(hours) {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    }
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days} day${days !== 1 ? 's' : ''}${remainingHours > 0 ? ` ${remainingHours}h` : ''}`;
  }

  formatMinutes(minutes) {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hour${hours !== 1 ? 's' : ''}${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
  }

  // Validate priority settings (updated to match controller validation)
  validatePrioritySettings(settings) {
    const errors = {};
    
    Object.entries(settings).forEach(([priority, times]) => {
      if (!times.response_time || times.response_time < 0.25 || times.response_time > 168) {
        errors[`${priority}_response_time`] = 'Response time must be between 0.25 and 168 hours (15 minutes to 1 week)';
      }
      
      if (!times.escalation_time || times.escalation_time < 1 || times.escalation_time > 336) {
        errors[`${priority}_escalation_time`] = 'Escalation time must be between 1 and 336 hours (1 hour to 2 weeks)';
      }
      
      if (times.escalation_time && times.response_time && times.escalation_time <= times.response_time) {
        errors[`${priority}_escalation_time`] = 'Escalation time must be greater than response time';
      }
    });
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validate auto-forward settings
  validateAutoForwardSettings(settings) {
    const errors = {};
    
    if (settings.auto_forward_time_minutes < 5 || settings.auto_forward_time_minutes > 1440) {
      errors.auto_forward_time_minutes = 'Forward time must be between 5 minutes and 24 hours';
    }
    
    if (settings.auto_forward_max_attempts < 1 || settings.auto_forward_max_attempts > 10) {
      errors.auto_forward_max_attempts = 'Max attempts must be between 1 and 10';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validate business hours
  validateBusinessHours(businessHours) {
    const errors = {};
    
    if (!businessHours.start_time || !businessHours.end_time) {
      errors.time = 'Both start and end times are required';
    } else if (businessHours.start_time >= businessHours.end_time) {
      errors.end_time = 'End time must be after start time';
    }
    
    if (!businessHours.days || businessHours.days.length === 0) {
      errors.days = 'At least one business day must be selected';
    }
    
    const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (businessHours.start_time && !timePattern.test(businessHours.start_time)) {
      errors.start_time = 'Invalid time format (use HH:MM)';
    }
    if (businessHours.end_time && !timePattern.test(businessHours.end_time)) {
      errors.end_time = 'Invalid time format (use HH:MM)';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new EscalationService();